import { authenticatedFetch } from "@lumi/data";
import { useCallback, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

/**
 * Maximum number of submissions that can be processed in a single bulk operation.
 * This limit is enforced both client-side and server-side for safety.
 */

/* eslint-disable max-lines-per-function */
export const BULK_OPERATION_MAX_ITEMS = 50;

export interface BulkOperationResult {
  success: number;
  failed: Array<{ id: string; title: string; error: string }>;
  total: number;
}

interface BulkApprovePayload {
  submissionIds: string[];
  notes?: string;
  publishImmediately?: boolean;
}

interface BulkRejectPayload {
  submissionIds: string[];
  reason: string;
  notes?: string;
}

interface UseBulkOperationsResult {
  bulkApprove: (payload: BulkApprovePayload) => Promise<BulkOperationResult>;
  bulkReject: (payload: BulkRejectPayload) => Promise<BulkOperationResult>;
  loading: boolean;
  error: string | null;
}

/**
 * Hook for bulk approval/rejection operations on submissions
 *
 * Provides functions to:
 * - Bulk approve multiple submissions with shared notes
 * - Bulk reject multiple submissions with shared reason and notes
 *
 * Features:
 * - Client-side validation (max items limit)
 * - Error handling with detailed failure information
 * - Loading state tracking
 */
export function useBulkOperations(): UseBulkOperationsResult {
  const { accessToken, refreshAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bulkApprove = useCallback(
    async (payload: BulkApprovePayload): Promise<BulkOperationResult> => {
      if (!accessToken) {
        throw new Error("Not authenticated");
      }

      if (payload.submissionIds.length === 0) {
        throw new Error("No submissions selected");
      }

      if (payload.submissionIds.length > BULK_OPERATION_MAX_ITEMS) {
        throw new Error(`Cannot approve more than ${BULK_OPERATION_MAX_ITEMS} submissions at once`);
      }

      setLoading(true);
      setError(null);

      try {
        const response = await authenticatedFetch<{
          message: string;
          results: {
            approved: number;
            failed: Array<{ id: string; title: string; error: string }>;
          };
        }>(
          `${API_BASE}/v1/admin/submissions/bulk-approve`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
          accessToken,
          createRefreshCallback(refreshAuth),
        );

        return {
          success: response.results.approved,
          failed: response.results.failed,
          total: payload.submissionIds.length,
        };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Bulk approval failed";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [accessToken, refreshAuth],
  );

  const bulkReject = useCallback(
    async (payload: BulkRejectPayload): Promise<BulkOperationResult> => {
      if (!accessToken) {
        throw new Error("Not authenticated");
      }

      if (payload.submissionIds.length === 0) {
        throw new Error("No submissions selected");
      }

      if (payload.submissionIds.length > BULK_OPERATION_MAX_ITEMS) {
        throw new Error(`Cannot reject more than ${BULK_OPERATION_MAX_ITEMS} submissions at once`);
      }

      if (!payload.reason) {
        throw new Error("Rejection reason is required");
      }

      setLoading(true);
      setError(null);

      try {
        const response = await authenticatedFetch<{
          message: string;
          results: {
            rejected: number;
            failed: Array<{ id: string; title: string; error: string }>;
          };
        }>(
          `${API_BASE}/v1/admin/submissions/bulk-reject`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
          accessToken,
          createRefreshCallback(refreshAuth),
        );

        return {
          success: response.results.rejected,
          failed: response.results.failed,
          total: payload.submissionIds.length,
        };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Bulk rejection failed";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [accessToken, refreshAuth],
  );

  return {
    bulkApprove,
    bulkReject,
    loading,
    error,
  };
}
